<template>
	<button
		v-if="notification && category[notification.category]"
		@click="redirectToTarget"
		:style="mainStyle"
		:id="'notification-' + notification._id"
		:aria-label="`Notification: ${$t(category[notification.category].label)} - ${$t(notification.messageKey, getMessageParams)}`"
	>
		<div class="notification-item-header">
			<div>
				<v-chip
					class="notification-item-chip"
					:color="category[notification.category].color"
					:class="[`text-${category[notification.category].textColor}`]"
					variant="flat"
				>
					<v-icon :color="category[notification.category].iconColor" start>
						{{ category[notification.category].icon }}
					</v-icon>
					<span>{{ $t(category[notification.category].label) }}</span>
				</v-chip>
				<v-icon v-if="notification.ignored && !actionTaken" class="clock-style">mdi-archive-clock</v-icon>
				<v-icon v-if="actionTaken" class="notification-item-state-indicator">mdi-check</v-icon>
				<v-icon v-else-if="actionRequired" class="notification-item-state-indicator">mdi-gesture-tap</v-icon>
				<v-chip
					v-if="notification.category === 'violation' && notification.params.category"
					class="notification-item-chip ml-1 text-red-darken-2"
					color="red-lighten-5"
					variant="flat"
				>
					<v-icon color="red-darken-2" start>mdi-shield-alert-outline</v-icon>
					<span>{{ $t(notification.params.category) }}</span>
				</v-chip>
			</div>
			<span :style="durationStyle">{{ getDuration(notification.createdAt) }}</span>
		</div>
		<div class="notification-item-content">
			<span>{{ $t(notification.messageKey, getMessageParams) }}</span>
			<v-btn
				v-if="size == 'sm'"
				icon="mdi-dots-vertical"
				variant="text"
				color="primary"
				size="x-small"
				:id="'menu-activator-' + notification._id"
				:disabled="isDisabled"
			></v-btn>

			<v-btn
				v-else-if="size == 'lg'"
				icon="mdi-dots-vertical"
				variant="text"
				color="primary"
				size="small"
				:id="'menu-activator-' + notification._id"
				:disabled="isDisabled"
				:aria-label="'Open menu for notification ' + notification._id"
				:title="'Open menu for notification ' + notification._id"
			></v-btn>
		</div>
		<v-menu :activator="'#menu-activator-' + notification._id">
			<div class="open-link" @click="menuHandler('open')">
				<v-icon color="grey-600">mdi-open-in-new</v-icon>
				<span>
					{{ $t('action_open') }}
				</span>
			</div>
		</v-menu>
		<div v-show="details" class="notification-item-footer">
			<v-chip v-show="userChipContent" class="notification-item-footer-chip" color="grey-lighten-3" variant="flat">
				<!--				TODO : existing bug in main but in vue 3 broken icons takes up the width of the words-->
				<!--				<v-icon color="grey-darken-3" start>mdi-face</v-icon>-->
				<span>{{ userChipContent }}</span>
			</v-chip>
			<v-chip v-show="getMessageParams.date" theme="dark" class="notification-item-footer-chip" color="grey-200" variant="flat">
				<v-icon color="grey-800" start>mdi-calendar-range</v-icon>
				<span>{{ getMessageParams.date }}</span>
			</v-chip>
			<v-chip v-show="getMessageParams.start && getMessageParams.end" theme="dark" class="notification-item-footer-chip" color="grey-200" variant="flat">
				<v-icon color="grey-800" start>mdi-calendar-range</v-icon>
				<span>{{ getMessageParams.start + ' - ' + getMessageParams.end }}</span>
			</v-chip>
			<v-chip v-show="false" class="notification-item-footer-chip" theme="dark" color="grey-200" variant="flat">
				<v-icon color="grey-800" start>mdi-clock-outline</v-icon>
				<span>Chip Content</span>
			</v-chip>
		</div>
	</button>
</template>

<script>
import i18n from '@/plugins/i18n';
import { DateTime } from 'luxon';
import { mapState, mapActions } from 'vuex';
import useDateTime from '../../../../components/composables/useDateTime';
import router from '../../../router';

export default {
	props: {
		notification: {
			type: Object,
			require: true,
		},
		size: {
			type: String,
			default: 'sm',
		},
		bg: {
			type: Boolean,
			default: false,
		},
		details: {
			type: Boolean,
			default: false,
		},
		clickable: {
			type: Boolean,
			default: true,
		},
		useRead: {
			type: Boolean,
			default: false,
		},
		active: {
			type: Boolean,
			default: false,
		},
	},
	setup() {
		const { formatDate, formatDateTime } = useDateTime();

		return {
			formatDate,
			formatDateTime,
		};
	},
	data() {
		return {
			selectedItem: 1,
			items: [{ key: 'open', text: this.$t('action_open'), icon: 'mdi-open-in-new' }],
			category: {
				dutyAssignment: {
					label: 'notification.category_dutyAssignment',
					color: 'warning-darken3',
					textColor: 'misc-white',
					icon: 'mdi-help-circle-outline',
					colorIcon: 'misc-white',
				},
				assignment: {
					label: 'notification.category_assignment',
					color: 'warning',
					textColor: 'misc-white',
					icon: 'mdi-help-circle-outline',
					colorIcon: 'misc-white',
				},
				violation: {
					label: 'notification.category_violation',
					color: 'error',
					textColor: 'white',
					icon: 'mdi-alert-outline',
					colorIcon: 'white',
				},
				approveAbsence: {
					label: 'notification.category_approveAbsence',
					color: 'grey-lighten-4',
					textColor: 'blue',
					icon: 'mdi-information-outline',
					colorIcon: 'info',
				},
				unsolvedViolations: {
					label: 'notification.category_unsolvedViolations',
					color: 'error',
					textColor: 'white',
					icon: 'mdi-help-circle-outline',
					colorIcon: 'white',
				},
				info: {
					label: 'notification.category_info',
					color: 'grey-lighten-4',
					textColor: 'blue',
					icon: 'mdi-information-outline',
					colorIcon: 'info',
				},
				exchangeRequest: {
					label: 'notification.category_exchangeRequest',
					color: 'grey-lighten-4',
					textColor: 'blue',
					icon: 'mdi-information-outline',
					colorIcon: 'info',
				},
				extraWorkRequest: {
					label: 'notification.category_extraWorkRequest',
					color: 'grey-lighten-4',
					textColor: 'blue',
					icon: 'mdi-information-outline',
					colorIcon: 'info',
				},
			},
		};
	},
	computed: {
		...mapState({
			user: (state) => state.auth.user,
			users: (state) => state.users.users,
		}),
		isDone() {
			let result = false;

			if (this.notification.done) {
				if (Object.keys(this.notification.done).length > 0) {
					result = true;
				}
			}

			return result;
		},
		actionTaken() {
			return this.notification.action && this.isDone;
		},
		actionRequired() {
			return this.notification.action && !this.isDone;
		},
		getMessageParams() {
			let params = {};

			for (let key in this.notification.messageParams) {
				let finalValue = '';

				//Convert messageParam if it's a date
				if (/\d{4}-\d{2}-\d{2}/.test(this.notification.messageParams[key])) {
					let date = new Date(this.notification.messageParams[key]);
					if (!isNaN(date)) {
						if (this.notification.category === 'extraWorkRequest') {
							finalValue = this.formatDateTime(date.toISOString());
						} else {
							finalValue = this.formatDate(date.toISOString());
						}
					}
				}

				//Get fullname of user
				if (key == 'user' || key == 'author') {
					let fullname = this.getUserFullname(this.notification.messageParams[key]);
					finalValue = fullname;
				}

				// Localize manual balance type
				if (key === 'manualBalanceType') {
					finalValue = i18n.t(`manualBalance_type_${this.notification.messageParams[key]}`);
				}

				if (finalValue == '') {
					//Just add messageParam raw
					finalValue = this.notification.messageParams[key];
				}

				params[key] = finalValue;
			}

			return params;
		},
		notificationBackground() {
			let backgroundColor = 'transparent';
			if (this.bg) {
				if (this.useRead && this.isRead(this.notification)) {
					backgroundColor = 'var(--v-grey-200-base)';
				} else {
					backgroundColor = 'var(--v-misc-white-base)';
				}
			}

			return backgroundColor;
		},
		mainStyle() {
			return {
				backgroundColor: this.notificationBackground,
				padding: '8px',
				margin: '5px',
				width: '100%',
				fontSize: this.size == 'sm' ? '13px' : '16px',
				textAlign: 'left',
				height: this.size == 'sm' ? '110px' : '100%',
			};
		},
		durationStyle() {
			return {
				fontSize: this.size == 'sm' ? '10px' : '13px',
			};
		},
		usersById() {
			const usersById = {};
			for (const user of this.users) {
				usersById[user.id] = user;
			}
			return usersById;
		},
		userChipContent() {
			return this.notification.params?.user && this.usersById[this.notification.params.user]
				? this.usersById[this.notification.params.user].displayName
				: this.getMessageParams.user;
		},
		isDisabled() {
			return !this.notification.target || this.notification.category === 'info';
		},
	},
	methods: {
		...mapActions({
			markNotifiactionAsRead: 'notification/markNotifiactionAsRead',
		}),
		isRead(notification) {
			let result = false;
			if (notification.read) {
				if (Object.keys(notification.read).length > 0) {
					if (notification.read[this.user.id]) {
						result = true;
					}
				}
			}

			return result;
		},
		getDuration(datetime) {
			let dt = DateTime.fromISO(datetime);
			let now = DateTime.now();
			let diff = now.diff(dt, ['seconds', 'minutes', 'hours', 'days', 'months', 'years']);

			if (diff.years > 0) {
				return this.$t('notification.duration_text', { duration: diff.years + 'y' });
			} else if (diff.months > 0) {
				return this.$t('notification.duration_text', { duration: diff.months + 'm' });
			} else if (diff.days > 0) {
				return this.$t('notification.duration_text', { duration: diff.days + 'd' });
			} else if (diff.hours > 0) {
				return this.$t('notification.duration_text', { duration: diff.hours + 'h' });
			} else if (diff.minutes > 0) {
				return this.$t('notification.duration_text', { duration: diff.minutes + 'min' });
			} else {
				return this.$t('notification.duration_now');
			}
		},
		getUserFullname(author) {
			let user = this.users.find((x) => x.username === author);
			if (user) {
				return user.displayName;
			}

			return '';
		},
		markAsRead() {
			if (!this.isRead(this.notification)) {
				this.markNotifiactionAsRead({ notificationId: this.notification._id });
			}
		},
		redirectToTarget() {
			if (this.clickable) {
				switch (this.notification.target) {
					case 'solution':
						this.markAsRead();
						if (router.currentRoute._value.path != '/solution/' + this.notification._id) {
							router.push({ path: '/solution/' + this.notification._id, replace: true });
						}
						this.$emit('closeNotifications');

						break;
					case 'absence':
						this.markAsRead();
						if (router.currentRoute._value.path != '/user/' + this.notification.params.user) {
							router.push({ path: '/user/' + this.notification.params.user, replace: true });
						}
						this.$emit('closeNotifications');

						break;
					default:
						this.markAsRead();
						console.log('Target not implemented');
						break;
				}
			}
		},
		menuHandler(action) {
			if (action == 'open') {
				this.redirectToTarget();
			}
		},
		addReadObserver() {
			const element = document.getElementById('notification-' + this.notification._id);
			if (!element) {
				return;
			}
			let observer = new IntersectionObserver(
				(entries, observer) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							setTimeout(() => {
								this.$emit('intersect', this.notification._id);
							}, 5000);
							observer.unobserve(entry.target);
						}
					});
				},
				{ threshold: 0.5 }
			);
			observer.observe(element);
		},
	},
	watch: {
		active(newVal) {
			if (newVal) {
				this.$nextTick(() => {
					this.addReadObserver();
				});
			}
		},
	},
	mounted() {
		this.addReadObserver();
	},
};
</script>

<style scoped>
.notification-item-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 5px;
}

.notification-item-chip {
	padding: 0 8px !important;
	height: auto !important;
}

.notification-item-chip .v-icon {
	margin-right: 3px !important;
	margin-left: -3px !important;
	font-size: 13px !important;
}

.notification-item-chip span {
	font-size: 10px !important;
}

.notification-item-state-indicator {
	margin-left: 3px;
	margin-top: -4px;
	font-size: 20px !important;
	color: rgba(0, 0, 0, 0.54) !important;
}

.clock-style {
	margin-left: 7px;
	font-size: 16px !important;
	color: rgba(0, 0, 0, 0.54) !important;
}

.notification-item-duration {
	font-size: 10px;
}

.notification-item-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.notification-item-footer-chip {
	height: 23px !important;
	margin-top: 5px;
	margin-right: 5px;
	padding: 8px 8px !important;
}

.notification-item-footer-chip .v-icon {
	margin-right: 3px !important;
	margin-left: -3px !important;
	font-size: 18px !important;
}

.notification-item-footer-chip span {
	font-size: 12px !important;
	color: rgb(var(--v-grey-800-base));
}

.open-link {
	width: 100px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 12px;
	justify-content: center;
	min-height: 32px;
	padding: 6px;
	border-radius: 4px;
	border: 1px solid lightgray;
}
</style>
